import { Facebook, Instagram, Linkedin } from 'lucide-react';

/**
 * Channel Capabilities Configuration
 *
 * Defines which triggers and actions are supported by each channel.
 * Used to validate workflow compatibility when selecting channels.
 */

// Channel display information with icons
export const channelInfo = {
  instagram: {
    label: 'Instagram',
    abbrev: 'IG',
    icon: Instagram,
    color: 'text-pink-500',
  },
  facebook: {
    label: 'Facebook',
    abbrev: 'FB',
    icon: Facebook,
    color: 'text-blue-500',
  },
  linkedin: {
    label: 'LinkedIn',
    abbrev: 'LI',
    icon: Linkedin,
    color: 'text-blue-600',
  },
};

/**
 * Get channel display info
 * @param {string} channel - Channel identifier
 * @returns {{ label: string, abbrev: string, icon: React.Component, color: string }}
 */
export const getChannelInfo = (channel) => {
  return channelInfo[channel] || { label: channel, abbrev: channel, icon: null, color: '' };
};

export const channelCapabilities = {
  triggers: {
    any_message: ['instagram', 'facebook'],
    message_text: ['instagram', 'facebook', 'linkedin'],
    comment_text: ['instagram', 'facebook'],
    contact_tag: ['instagram', 'facebook', 'linkedin'],
    schedule: ['instagram', 'facebook'],
    linkedin_post_comment: ['linkedin'],
  },
  actions: {
    send_message: ['instagram', 'facebook', 'linkedin'],
    reply_comment: ['instagram', 'facebook'],
    contact_add_tag: ['instagram', 'facebook', 'linkedin'],
    contact_remove_tag: ['instagram', 'facebook', 'linkedin'],
    contact_state_change: ['instagram', 'facebook', 'linkedin'],
    contact_change_owner: ['instagram', 'facebook', 'linkedin'],
    trigger_webhook: ['instagram', 'facebook', 'linkedin'],
    ai_select_message: ['instagram', 'facebook', 'linkedin'],
    delay: ['instagram', 'facebook', 'linkedin'],
    branch: ['instagram', 'facebook', 'linkedin'],
    linkedin_reply_comment: ['linkedin'],
  },
};

// Human-readable labels for trigger types
const triggerLabels = {
  any_message: 'Any Message',
  message_text: 'Message Text',
  comment_text: 'Comment Text',
  contact_tag: 'Contact Tag',
  schedule: 'Schedule',
  linkedin_post_comment: 'LinkedIn Post Comment',
};

// Human-readable labels for action types
const actionLabels = {
  send_message: 'Send Message',
  reply_comment: 'Reply to Comment',
  contact_add_tag: 'Add Contact Tag',
  contact_remove_tag: 'Remove Contact Tag',
  contact_state_change: 'Change Contact State',
  contact_change_owner: 'Change Contact Owner',
  trigger_webhook: 'Trigger Webhook',
  ai_select_message: 'AI Pick',
  delay: 'Delay',
  branch: 'Branch',
  linkedin_reply_comment: 'LinkedIn Reply Comment',
};

// Human-readable labels for channels
const channelLabels = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
};

/**
 * Get human-readable label for a trigger type
 * @param {string} type - Trigger type
 * @returns {string} Human-readable label
 */
export const getTriggerLabel = (type) => {
  return triggerLabels[type] || type;
};

/**
 * Get human-readable label for an action type
 * @param {string} type - Action type
 * @returns {string} Human-readable label
 */
export const getActionLabel = (type) => {
  return actionLabels[type] || type;
};

/**
 * Get human-readable label for a channel
 * @param {string} channel - Channel identifier
 * @returns {string} Human-readable label
 */
export const getChannelLabel = (channel) => {
  return channelLabels[channel] || channel;
};

/**
 * Validate if workflow steps are compatible with selected channels
 *
 * @param {Array} trigger - Trigger array from workflow store (tempTrigger)
 * @param {Array} nodes - Nodes array from workflow store (tempNodes)
 * @param {string[]} selectedChannels - Channels being selected
 * @returns {{ isValid: boolean, issues: Array<{type: string, name: string, label: string, unsupportedChannels: string[]}> }}
 */
export const validateChannelCompatibility = (
  trigger,
  nodes,
  selectedChannels
) => {
  const issues = [];

  // If no channels selected, nothing to validate against
  if (!selectedChannels || selectedChannels.length === 0) {
    return { isValid: true, issues: [] };
  }

  // Check trigger compatibility
  if (trigger?.length > 0) {
    const triggerType = trigger[0].type;
    const supportedChannels = channelCapabilities.triggers[triggerType] || [];

    // Find channels that don't support this trigger
    const unsupportedChannels = selectedChannels.filter(
      (ch) => !supportedChannels.includes(ch)
    );

    if (unsupportedChannels.length > 0) {
      issues.push({
        type: 'trigger',
        name: triggerType,
        label: getTriggerLabel(triggerType),
        unsupportedChannels: unsupportedChannels.map(getChannelLabel),
      });
    }
  }

  // Check action nodes compatibility
  if (nodes?.length > 0) {
    nodes.forEach((node) => {
      // Only check action nodes with an action type
      if (node.type === 'action' && node.data?.actionType) {
        const actionType = node.data.actionType;
        const supportedChannels = channelCapabilities.actions[actionType] || [];

        // Find channels that don't support this action
        const unsupportedChannels = selectedChannels.filter(
          (ch) => !supportedChannels.includes(ch)
        );

        if (unsupportedChannels.length > 0) {
          // Avoid duplicate issues for the same action type
          const existingIssue = issues.find(
            (i) => i.type === 'action' && i.name === actionType
          );
          if (!existingIssue) {
            issues.push({
              type: 'action',
              name: actionType,
              label: getActionLabel(actionType),
              unsupportedChannels: unsupportedChannels.map(getChannelLabel),
            });
          }
        }
      }
    });
  }

  return {
    isValid: issues.length === 0,
    issues,
  };
};
